import * as React from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";

/**
 * Badge component (shadcn/ui style)
 *
 * A small status indicator / type-category chip component with multiple
 * variants and sizes, restyled for the Institutional Console redesign
 * (DESIGN-SPEC.md §4.3/§4.7): 4px radius (no more pill/9999px shape, per
 * spec §2.4 "no pill radii except status dots and avatar"), 10.5px 600-weight
 * uppercase text with 0.04em tracking, tinted bg + tinted text per variant.
 * No border by default (mockup chips are borderless tinted fills) — `outline`
 * keeps a visible border for callers that relied on it.
 *
 * Color mapping (DESIGN-SPEC §4.3 "Type/category badges"): Stock/ETF -> accent
 * (`default`), Crypto -> warning, Metal -> positive (`success`), neutral ->
 * flat translucent white (`secondary`).
 *
 * @example
 * <Badge>Default</Badge>
 * <Badge variant="success">Active</Badge>
 * <Badge variant="destructive" size="lg">Error</Badge>
 */

const badgeVariants = cva(
  "inline-flex items-center justify-center rounded font-semibold uppercase tracking-[0.04em] transition-colors duration-[120ms] ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        // Stock/ETF -> accent tint (§4.3)
        default:
          "border border-transparent bg-[var(--color-state-info-bg)] text-[var(--color-state-info-text)] hover:bg-[var(--color-state-info-bg)]/80",
        // Neutral -> flat translucent fill (§4.3 "neutral -> rgba(255,255,255,0.05)").
        // Issue #238 fix: was a hardcoded dark-only rgba(255,255,255,0.05),
        // which vanishes (white-on-white) on console-light's white --surface
        // (deferred #236 review finding N1). Routed through theme-flipping
        // --badge-neutral-bg/-hover tokens (console-dark.css keeps the
        // original dark value; console-light.css gets a visible neutral tint).
        secondary:
          "border border-transparent bg-[var(--badge-neutral-bg,rgba(255,255,255,0.05))] text-[var(--color-text-secondary)] hover:bg-[var(--badge-neutral-bg-hover,rgba(255,255,255,0.09))]",
        destructive:
          "border border-transparent bg-[var(--color-state-error-bg)] text-[var(--color-state-error-text)] hover:bg-[var(--color-state-error-bg)]/80",
        outline: "border border-[var(--color-border)] text-[var(--color-text-primary)] bg-transparent",
        // Metal -> positive tint (§4.3)
        success:
          "border border-transparent bg-[var(--color-state-success-bg)] text-[var(--color-state-success-text)] hover:bg-[var(--color-state-success-bg)]/80",
        // Crypto -> warning tint (§4.3)
        warning:
          "border border-transparent bg-[var(--color-state-warning-bg)] text-[var(--color-state-warning-text)] hover:bg-[var(--color-state-warning-bg)]/80",
        info:
          "border border-transparent bg-[var(--color-state-info-bg)] text-[var(--color-state-info-text)] hover:bg-[var(--color-state-info-bg)]/80",
      },
      size: {
        // §4.3: 10.5px, 600 weight, uppercase. Padding kept compact per mockup chip() helper (2px 8px).
        sm: "px-2 py-0.5 text-[10.5px]",
        md: "px-2.5 py-0.5 text-[10.5px]",
        lg: "px-3 py-1 text-[11px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

/**
 * @param {Object} props
 * @param {'default'|'secondary'|'destructive'|'outline'|'success'|'warning'|'info'} props.variant
 * @param {'sm'|'md'|'lg'} props.size
 * @param {string} props.className
 */
const Badge = React.forwardRef(
  ({ className, variant, size, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  )
);
Badge.displayName = "Badge";

/**
 * DotBadge - Badge with a status dot indicator
 */
const DotBadge = React.forwardRef(
  ({ className, variant = "default", children, ...props }, ref) => {
    const dotColors = {
      default: "bg-primary",
      secondary: "bg-secondary-foreground",
      destructive: "bg-destructive",
      success: "bg-success",
      warning: "bg-warning",
      info: "bg-info",
      outline: "bg-foreground",
    };

    return (
      <div
        ref={ref}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium text-foreground",
          className
        )}
        {...props}
      >
        <span
          className={cn("h-1.5 w-1.5 rounded-full", dotColors[variant])}
          aria-hidden="true"
        />
        {children}
      </div>
    );
  }
);
DotBadge.displayName = "DotBadge";

/**
 * CountBadge - Badge for displaying counts (notifications, items, etc.)
 *
 * §4.7 "Status & badges": count badges (unread) are mono 10px on a red tint
 * (`rgba(229,72,77,0.15)` / text `#f0736f`) — both values are already what
 * the `destructive` badgeVariant resolves to via --color-state-error-bg/-text
 * in console-dark.css/console-light.css, so only the font (mono, 10px,
 * non-uppercase — counts are numerals, not labels) changes here relative to
 * the base Badge.
 */
const CountBadge = React.forwardRef(
  ({ className, count, max = 99, variant = "destructive", size = "sm", ...props }, ref) => {
    const displayCount = count > max ? `${max}+` : count;

    return (
      <Badge
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          "min-w-[1.25rem] justify-center font-mono normal-case tracking-normal tabular-nums text-[10px]",
          className
        )}
        {...props}
      >
        {displayCount}
      </Badge>
    );
  }
);
CountBadge.displayName = "CountBadge";

export { Badge, badgeVariants, DotBadge, CountBadge };
